














import { computed, defineComponent, ref } from "@vue/composition-api";
import { ProfileLink } from "@scrile/api-provider/dist/api/ProfileLinksProvider";
import useOneTimePurchase from "@/hooks/useOneTimePurchase";
import {
  getProfileLinkUrl,
  profileLinkToContentPriceSubject,
  profileLinkToPurchasePriceSubject,
} from "@/hooks/useProfileLinks";
import createDeferred from "@/lib/deferredPromise";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCostByCurrency, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { openInNewTab } from "@/lib/urlHelpers";
import { PurchaseDetails } from "@/payments/types";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";

export default defineComponent({
  name: "ConfirmPurchaseProfileLink",
  components: { BaseButton, BaseConfirm },
  props: {
    profileLink: { type: Object as () => ProfileLink | null, default: null },
  },
  setup(props, { emit }) {
    const loading = ref(false);

    const price = computed(() =>
      props.profileLink?.pricePackage ? formatMoneyString(props.profileLink?.pricePackage.amount) : ""
    );
    const linkName = computed(() => (props.profileLink ? $t(`profileLink.${props.profileLink.type}`) : ""));
    const subject = computed(() => (props.profileLink ? profileLinkToContentPriceSubject(props.profileLink) : null));
    const cost = computed(() =>
      props.profileLink?.pricePackage ? getCostByCurrency(props.profileLink?.pricePackage)?.cost ?? 0 : 0
    );
    const purchaseDetails = computed<PurchaseDetails | null>(() =>
      props.profileLink
        ? {
            pricePackageId: props.profileLink.pricePackageId,
            price: formatMoneyString(cost.value, { currencyCode: getMainCurrencyCode() }),
            subject: profileLinkToPurchasePriceSubject(props.profileLink),
          }
        : null
    );

    const { performOneTimePurchase } = useOneTimePurchase(subject, purchaseDetails);

    const purchase = async () => {
      try {
        loading.value = true;
        const response = await performOneTimePurchase();
        if (response.success) {
          const { promise, resolve } = createDeferred<ProfileLink | undefined>();
          emit("purchased", (links: ProfileLink[]) => resolve(links.find(i => props.profileLink?.id === i.id)));
          const link = await promise;
          if (link) {
            openInNewTab(getProfileLinkUrl(link));
          }
          emit("close");
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      price,
      linkName,
      loading,
      purchase,
    };
  },
});
